/* eslint-disable @typescript-eslint/no-unused-vars */
// src/pages/admin/EventManager.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../../context/DarkModeContext";
import { eventService } from "../../service/eventService";
import type { Event, EventStatus } from "../../types/event";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Users,
  Calendar,
  Eye,
  Download,
} from "lucide-react";
import {
  SkeletonEventManagerLoading,
  SkeletonEventErrorState,
  SkeletonEventEmptyState,
} from "../../components/Skeleton";

const EventManager: React.FC = () => {
  const navigate = useNavigate();
  const { isDark } = useDarkMode();
  const [events, setEvents] = useState<Event[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<Event[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<EventStatus | "all">("all");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    loadEvents();
  }, [retryCount]);

  useEffect(() => {
    let filtered = events;

    if (searchTerm) {
      filtered = filtered.filter(
        (event) =>
          event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          event.description?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((event) => event.status === statusFilter);
    }

    setFilteredEvents(filtered);
  }, [searchTerm, statusFilter, events]);

  const loadEvents = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const eventsData = await eventService.getEvents();
      setEvents(eventsData);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load events";
      setError(errorMessage);
      console.error("Error loading events:", err);

      // Auto-retry logic
      if (retryCount < 3) {
        setTimeout(() => {
          setRetryCount((prev) => prev + 1);
        }, 2000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;

    try {
      await eventService.deleteEvent(eventId);
      await loadEvents();
    } catch (err) {
      setError("Failed to delete event");
    }
  };

  const handleStatusChange = async (
    eventId: string,
    newStatus: EventStatus,
  ) => {
    try {
      await eventService.updateEvent(eventId, { status: newStatus });
      await loadEvents();
    } catch (err) {
      setError("Failed to update event status");
    }
  };

  const exportEvents = () => {
    const csv = [
      [
        "Title",
        "Type",
        "Platform",
        "Start Date",
        "End Date",
        "Audience",
        "Status",
        "Registrations",
      ],
      ...filteredEvents.map((event) => [
        event.title,
        event.type,
        event.platform,
        new Date(event.startDate).toLocaleString(),
        new Date(event.endDate).toLocaleString(),
        event.audience,
        event.status,
        event.registeredCount?.toString() || "0",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "events-export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleCreateEvent = () => {
    navigate("/dashboard/create-event");
  };

  const handleEditEvent = (eventId: string) => {
    navigate(`/dashboard/edit-event/${eventId}`);
  };

  const handleViewEvent = (eventId: string) => {
    navigate(`/event-calendar?event=${eventId}`);
  };

  const handleRetry = () => {
    setRetryCount(0);
    setError(null);
    setIsLoading(true);
  };

  if (isLoading) {
    return <SkeletonEventManagerLoading theme={isDark ? "dark" : "light"} />;
  }

  if (error) {
    return (
      <SkeletonEventErrorState
        theme={isDark ? "dark" : "light"}
        className="relative"
      >
        <div className="text-center py-12">
          <div
            className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
              isDark ? "bg-red-900/20 text-red-400" : "bg-red-100 text-red-600"
            }`}
          >
            <svg
              className="w-8 h-8"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2
            className={`text-2xl font-bold mb-4 ${
              isDark ? "text-red-400" : "text-red-800"
            }`}
          >
            Error Loading Events
          </h2>
          <p className={`mb-6 ${isDark ? "text-red-300" : "text-red-600"}`}>
            {error}
          </p>
          <p
            className={`text-sm mb-6 ${isDark ? "text-gray-400" : "text-gray-500"}`}
          >
            Retry attempt: {retryCount}/3
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={handleRetry}
              className={`px-6 py-3 rounded-lg font-medium shadow-md transition-all duration-300 ${
                isDark
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
                  : "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white"
              }`}
            >
              Try Again
            </button>
            <button
              onClick={() => navigate("/dashboard")}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
              }`}
            >
              Go to Dashboard
            </button>
          </div>
        </div>
      </SkeletonEventErrorState>
    );
  }

  return (
    <div
      className={`min-h-screen py-8 px-4 sm:px-6 lg:px-8 transition-colors duration-300 ${
        isDark
          ? "bg-gradient-to-br from-gray-900 to-gray-800 text-gray-100"
          : "bg-gradient-to-br from-sky-50 to-blue-50 text-gray-900"
      }`}
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-8">
          <div className="mb-4 lg:mb-0">
            <h1
              className={`text-3xl sm:text-4xl font-bold bg-gradient-to-r ${
                isDark
                  ? "from-blue-400 to-blue-600 text-white"
                  : "from-sky-500 to-blue-600 text-transparent bg-clip-text"
              }`}
            >
              Event Management
            </h1>
            <p className={`mt-2 ${isDark ? "text-gray-400" : "text-blue-700"}`}>
              Create and manage events for the community
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={exportEvents}
              className={`px-4 py-2 rounded-lg border flex items-center space-x-2 transition-all duration-300 ${
                isDark
                  ? "border-gray-600 text-gray-300 hover:bg-gray-800 hover:border-gray-500 hover:text-white"
                  : "border-blue-300 text-blue-600 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-700"
              }`}
            >
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
            <button
              onClick={handleCreateEvent}
              className={`px-4 py-2 rounded-lg font-semibold flex items-center space-x-2 transition-all duration-300 hover:shadow-lg ${
                isDark
                  ? "bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white"
                  : "bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700 text-white"
              }`}
            >
              <Plus className="w-4 h-4" />
              <span>Create Event</span>
            </button>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className={`mb-6 p-4 rounded-lg transition-all duration-300 ${
              isDark
                ? "bg-red-900/20 border border-red-800"
                : "bg-red-50 border border-red-200"
            }`}
          >
            <div className="flex items-center justify-between">
              <p className={isDark ? "text-red-400" : "text-red-600"}>
                {error}
              </p>
              <button
                onClick={handleRetry}
                className={`px-3 py-1 rounded text-sm font-medium ${
                  isDark
                    ? "bg-red-800 hover:bg-red-700 text-white"
                    : "bg-red-100 hover:bg-red-200 text-red-700"
                }`}
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Filters */}
        <div
          className={`rounded-xl p-6 mb-6 transition-all duration-300 ${
            isDark
              ? "bg-gray-800 border border-gray-700 hover:border-gray-600"
              : "bg-white border border-blue-200 shadow-lg hover:shadow-xl hover:border-blue-300"
          }`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <Search
                className={`absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 transition-colors duration-300 ${
                  isDark
                    ? "text-gray-400 group-hover:text-blue-400"
                    : "text-blue-500 group-hover:text-blue-600"
                }`}
              />
              <input
                type="text"
                placeholder="Search events..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rounded-lg border transition-all duration-300 group ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400 hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                    : "bg-white border-blue-200 text-gray-900 placeholder-blue-400 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                }`}
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(e.target.value as EventStatus | "all")
              }
              className={`px-4 py-2 rounded-lg border transition-all duration-300 ${
                isDark
                  ? "bg-gray-700 border-gray-600 text-white hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                  : "bg-white border-blue-200 text-gray-900 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
              }`}
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="scheduled">Scheduled</option>
              <option value="live">Live</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>

        {/* Events Table */}
        {filteredEvents.length === 0 && !isLoading && !error ? (
          <SkeletonEventEmptyState theme={isDark ? "dark" : "light"} />
        ) : (
          <div
            className={`rounded-xl overflow-hidden transition-all duration-300 ${
              isDark
                ? "bg-gray-800 border border-gray-700 hover:border-gray-600"
                : "bg-white border border-blue-200 shadow-lg hover:shadow-xl hover:border-blue-300"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={isDark ? "bg-gray-700" : "bg-blue-50"}>
                    <th className="px-6 py-4 text-left font-semibold text-sm sm:text-base">
                      Event
                    </th>
                    <th className="px-6 py-4 text-left font-semibold text-sm sm:text-base">
                      Type
                    </th>
                    <th className="px-6 py-4 text-left font-semibold text-sm sm:text-base">
                      Date & Time
                    </th>
                    <th className="px-6 py-4 text-left font-semibold text-sm sm:text-base">
                      Audience
                    </th>
                    <th className="px-6 py-4 text-left font-semibold text-sm sm:text-base">
                      Registrations
                    </th>
                    <th className="px-6 py-4 text-left font-semibold text-sm sm:text-base">
                      Status
                    </th>
                    <th className="px-6 py-4 text-left font-semibold text-sm sm:text-base">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredEvents.map((event) => (
                    <tr
                      key={event.id}
                      className={`border-t transition-colors duration-300 ${
                        isDark
                          ? "border-gray-700 hover:bg-gray-750"
                          : "border-blue-100 hover:bg-blue-50"
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <h3
                            className={`font-semibold text-sm sm:text-base ${isDark ? "text-white" : "text-blue-800"}`}
                          >
                            {event.title}
                          </h3>
                          {event.description && (
                            <p
                              className={`text-xs sm:text-sm mt-1 ${isDark ? "text-gray-400" : "text-blue-600"}`}
                            >
                              {event.description.length > 60
                                ? `${event.description.substring(0, 60)}...`
                                : event.description}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            isDark
                              ? "bg-blue-600/20 text-blue-400 border border-blue-500/30"
                              : "bg-blue-100 text-blue-600 border border-blue-200"
                          }`}
                        >
                          {event.type}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className={`text-xs sm:text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                        >
                          <div className="font-medium">
                            {new Date(event.startDate).toLocaleDateString(
                              "en-US",
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                              },
                            )}
                          </div>
                          <div
                            className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                          >
                            {new Date(event.startDate).toLocaleTimeString(
                              "en-US",
                              {
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            isDark
                              ? "bg-green-600/20 text-green-400 border border-green-500/30"
                              : "bg-green-100 text-green-600 border border-green-200"
                          }`}
                        >
                          {event.audience}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <Users
                            className={`w-4 h-4 transition-colors duration-300 ${
                              isDark
                                ? "text-gray-400 group-hover:text-blue-400"
                                : "text-blue-500 group-hover:text-blue-600"
                            }`}
                          />
                          <span
                            className={`text-sm ${isDark ? "text-gray-300" : "text-gray-700"}`}
                          >
                            {event.registeredCount || 0}
                            {event.maxAttendees && ` / ${event.maxAttendees}`}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <select
                          value={event.status}
                          onChange={(e) =>
                            handleStatusChange(
                              event.id,
                              e.target.value as EventStatus,
                            )
                          }
                          className={`px-2 py-1 rounded border text-xs sm:text-sm transition-all duration-300 ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white hover:border-blue-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                              : "bg-white border-blue-200 text-gray-900 hover:border-blue-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
                          }`}
                        >
                          <option value="draft">Draft</option>
                          <option value="scheduled">Scheduled</option>
                          <option value="live">Live</option>
                          <option value="completed">Completed</option>
                          <option value="cancelled">Cancelled</option>
                        </select>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleEditEvent(event.id)}
                            className={`p-2 rounded transition-all duration-300 hover:scale-110 ${
                              isDark
                                ? "hover:bg-gray-700 text-blue-400 hover:text-blue-300"
                                : "hover:bg-blue-100 text-blue-600 hover:text-blue-700"
                            }`}
                            title="Edit Event"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleViewEvent(event.id)}
                            className={`p-2 rounded transition-all duration-300 hover:scale-110 ${
                              isDark
                                ? "hover:bg-gray-700 text-green-400 hover:text-green-300"
                                : "hover:bg-green-100 text-green-600 hover:text-green-700"
                            }`}
                            title="View Event"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEvent(event.id)}
                            className={`p-2 rounded transition-all duration-300 hover:scale-110 ${
                              isDark
                                ? "hover:bg-gray-700 text-red-400 hover:text-red-300"
                                : "hover:bg-red-100 text-red-600 hover:text-red-700"
                            }`}
                            title="Delete Event"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredEvents.length === 0 && (
                <div
                  className={`text-center py-12 transition-colors duration-300 ${
                    isDark ? "text-gray-500" : "text-blue-500"
                  }`}
                >
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-medium">No events found</p>
                  <p
                    className={`mt-2 text-sm ${isDark ? "text-gray-400" : "text-blue-400"}`}
                  >
                    Try adjusting your search or filter criteria
                  </p>
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setStatusFilter("all");
                    }}
                    className={`mt-4 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                      isDark
                        ? "bg-gray-700 hover:bg-gray-600 text-white"
                        : "bg-blue-100 hover:bg-blue-200 text-blue-700"
                    }`}
                  >
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Statistics Footer */}
        {filteredEvents.length > 0 && (
          <div
            className={`mt-6 p-4 rounded-lg transition-all duration-300 ${
              isDark
                ? "bg-gray-800/50 border border-gray-700"
                : "bg-blue-50/50 border border-blue-100"
            }`}
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <p
                  className={`text-sm ${isDark ? "text-gray-400" : "text-blue-600"}`}
                >
                  Total Events
                </p>
                <p
                  className={`text-2xl font-bold ${isDark ? "text-white" : "text-blue-800"}`}
                >
                  {events.length}
                </p>
              </div>
              <div className="text-center">
                <p
                  className={`text-sm ${isDark ? "text-gray-400" : "text-blue-600"}`}
                >
                  Live Events
                </p>
                <p
                  className={`text-2xl font-bold ${isDark ? "text-white" : "text-blue-800"}`}
                >
                  {events.filter((e) => e.status === "live").length}
                </p>
              </div>
              <div className="text-center">
                <p
                  className={`text-sm ${isDark ? "text-gray-400" : "text-blue-600"}`}
                >
                  Total Registrations
                </p>
                <p
                  className={`text-2xl font-bold ${isDark ? "text-white" : "text-blue-800"}`}
                >
                  {events.reduce(
                    (sum, event) => sum + (event.registeredCount || 0),
                    0,
                  )}
                </p>
              </div>
              <div className="text-center">
                <p
                  className={`text-sm ${isDark ? "text-gray-400" : "text-blue-600"}`}
                >
                  Showing
                </p>
                <p
                  className={`text-2xl font-bold ${isDark ? "text-white" : "text-blue-800"}`}
                >
                  {filteredEvents.length}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventManager;
